import { getMerchantGeneralSettings, putMerchantGeneralSettings } from 'APIs/merchants/merchants';
import { MerchantGeneralSettingsResponse } from 'APIs/merchants/merchants.types';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import getApiErrorMessage from 'utils/getApiErrorMsg';

import CheckoutLimitsData from './CheckoutLimitsData';
import { UpdateCheckoutLimitsFormProps, toPutMerchantGeneralSettingsRequest } from './types';

type Props = {
  merchantId: string;
};

const CheckoutLimits = ({ merchantId }: Props) => {
  const { apiAprilBaseUri } = useSelector((state: ReduxStateType) => ({
    apiAprilBaseUri: state.apiAprilBaseUri,
  }));

  const [generalSettings, setGeneralSettings] = useState<MerchantGeneralSettingsResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [modalErrorMsg, setModalErrorMsg] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);

  const fetchCheckoutLimits = useCallback(async () => {
    if (!apiAprilBaseUri) {
      return;
    }

    try {
      setIsLoading(true);
      const resp = await getMerchantGeneralSettings(apiAprilBaseUri, merchantId);
      setGeneralSettings(resp);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }, [apiAprilBaseUri, merchantId]);

  const updateCheckoutLimits = useCallback(
    async (formData: UpdateCheckoutLimitsFormProps) => {
      if (!generalSettings || !apiAprilBaseUri) {
        return;
      }
      const payload = toPutMerchantGeneralSettingsRequest(generalSettings, formData);
      setIsModalLoading(true);

      try {
        await putMerchantGeneralSettings(apiAprilBaseUri, merchantId, payload);
        fetchCheckoutLimits();
        setModalErrorMsg('');
        setShowModal(false);
        toast.success('Checkout limits updated successfully');
      } catch (e) {
        setModalErrorMsg(getApiErrorMessage(e));
      } finally {
        setIsModalLoading(false);
      }
    },
    [generalSettings, apiAprilBaseUri, merchantId, fetchCheckoutLimits],
  );

  useEffect(() => {
    if (!generalSettings) {
      fetchCheckoutLimits();
    }
  }, [generalSettings, apiAprilBaseUri, merchantId, fetchCheckoutLimits]);

  return (
    <CheckoutLimitsData
      generalSettings={generalSettings}
      isLoading={isLoading}
      isModalLoading={isModalLoading}
      modalError={modalErrorMsg}
      showModal={showModal}
      setShowModal={setShowModal}
      updateCheckoutLimitsHandler={updateCheckoutLimits}
    />
  );
};

export default CheckoutLimits;
